import { TGetConversationByUserOutputDto } from "@/api/services/main/conversation/dtos/get-conversation-by-user.dto";
import { TGetMessageOutputDto } from "@/api/services/main/conversation/dtos/get-message-list-by-conversation.dto";
import { EVENTS } from "@/constants/socket";
import { TMessageBoxProps } from "@/features/chat/components/MessageBox";
import { useRoom } from "@/features/chat/hooks/useRoom";
import { useSocket } from "@/features/chat/providers/SocketProvider";
import { FCC } from "@/types/common";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

type TChatContext = {
  currentRoom: {
    roomData: TGetConversationByUserOutputDto | null;
    oldMessages: TMessageBoxProps[];
    newMessages: TMessageBoxProps[];
    hasNextPage: boolean;
    isFetching: boolean;
    fetchNextPage: () => Promise<void>;
    sendMsg: (msg: string, filePaths: string[]) => Promise<void>;
    readMsg: () => Promise<void>;
    deleteMsg: (id: number) => Promise<void>;
    roomRef: React.RefObject<HTMLDivElement>;
    scrollToBottom: () => void;
  };
};

const ChatContext = createContext<TChatContext>({} as TChatContext);

export const ChatProvider: FCC = ({ children }) => {
  const [searchParams] = useSearchParams();
  const chatStatus = searchParams.get("chat");
  const currentRoom = useRoom();
  const { socket } = useSocket();

  useEffect(() => {
    if (socket) {
      socket.on(EVENTS.CONVERSATION.NEW_MESSAGE, (receivedMessage: TGetMessageOutputDto) => {
        window.dispatchEvent(new CustomEvent(EVENTS.CONVERSATION.NEW_MESSAGE, { detail: receivedMessage }));
      });

      return () => {
        socket.off(EVENTS.CONVERSATION.NEW_MESSAGE);
      };
    }
  }, [socket]);

  const contextValue = useMemo(
    () => ({
      currentRoom,
    }),
    [currentRoom, chatStatus],
  );

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};

export const ChatConsumer = ChatContext.Consumer;

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within ChatProvider");
  }
  return context;
};
